import { Card } from 'antd';
import React, { createContext, FC, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PAGE_ID } from '@/infrastructure/reporter/datong/id';
import { useReportPage } from '@/infrastructure/reporter/datong/report';
import { Platform } from '@/infrastructure/utils/app';
import { Loading } from '@/presentation/components/loading';
import { getRoleByUser } from '@/presentation/components/user-manage/api';
import {
  ConfigItem,
  getConfigList,
  getDefaultConfig,
  IConfigListReq,
  isConfigManage,
} from '@/presentation/pages/setting/app-deliver-config/api';
import { ConfigHeader } from '@/presentation/pages/setting/app-deliver-config/config-header';
import { ConfigList } from '@/presentation/pages/setting/app-deliver-config/config-list';
import { ConfigEmpty } from '@/presentation/pages/setting/app-deliver-config/empty';
import {
  getConfigDocUrl,
  getConfigTab,
} from '@/presentation/pages/setting/app-deliver-config/utils';
import { useStores } from '@/presentation/store';

interface IPlatform {
  platform?: Platform;
  setPlatform?: React.Dispatch<React.SetStateAction<Platform>>;
  addPlatform?: Platform;
  setAddPlatform?: React.Dispatch<React.SetStateAction<Platform>>;
}

export const PlatformContext = createContext<IPlatform>({});

const AppDeliverConfig: FC = () => {
  useReportPage(PAGE_ID.setting.appDeliverConfig);
  const [configList, setConfigList] = useState<ConfigItem[]>([]);
  const [configNum, setConfigNum] = useState(0);
  const defaultPage = 1;
  const defaultPageSize = 10;
  const [page, setPage] = useState(defaultPage);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [total, setTotal] = useState(0);
  const [firstLoading, setFirstLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [defaultConfig, setDefaultConfig] = useState('');
  const [auth, setAuth] = useState(false);
  const { pathname } = useLocation();
  const tab = getConfigTab(pathname);
  // 拉取配置列表平台
  const [platform, setPlatform] = useState<Platform>(Platform.Android);
  // 新建配置选择平台
  const [addPlatform, setAddPlatform] = useState<Platform>(Platform.Android);
  const { loadOperation } = useStores().ConfigStore;

  const loadData = async (req: IConfigListReq) => {
    setLoading(true);
    const data = await getConfigList(tab, req);
    setConfigList(data.config);
    setConfigNum(parseInt(data.total, 10));
    setTotal(parseInt(data.total, 10));
    setLoading(false);
    setFirstLoading(false);
  };

  const loadDefaultConfig = async (platform?: Platform) => {
    const defaultConfig = await getDefaultConfig(tab, platform ?? addPlatform);
    setDefaultConfig(defaultConfig);
  };

  const loadAuth = async () => {
    const auth = await getRoleByUser();
    setAuth(auth);
  };

  useEffect(() => {
    loadData({
      name: search,
      page,
      pageSize,
      platform,
    });
  }, [tab, page, pageSize, search, platform]);

  useEffect(() => {
    loadDefaultConfig();
    loadAuth();
  }, [tab, addPlatform]);

  useEffect(() => {
    const load = async () => {
      await loadOperation();
    };
    load();
  }, []);

  const onPageChange = async (page: number, pageSize?: number) => {
    setPage(page);
    setPageSize(pageSize ?? defaultPageSize);
  };

  const onSearch = (search: string) => {
    setPage(defaultPage);
    setSearch(search);
  };

  const update = () => {
    loadData({
      name: search,
      page,
      pageSize,
      platform,
    });
  };

  if (firstLoading) {
    return <Loading />;
  }

  return (
    <Card bordered={false}>
      <PlatformContext.Provider value={{
        platform,
        setPlatform,
        addPlatform,
        setAddPlatform,
      }}>
      {(configList.length === 0 && !search && !isConfigManage(tab))
        ? <ConfigEmpty
            tab={tab}
            update={update}
            defaultConfig={defaultConfig}
            configDocUrl={getConfigDocUrl(tab)}
          />
        : <>
            <ConfigHeader
              tab={tab}
              configNum={configNum}
              update={update}
              defaultConfig={defaultConfig}
              onSearch={onSearch}
              configDocUrl={getConfigDocUrl(tab)}
              isAdmin={auth}
            />
            <ConfigList
              tab={tab}
              loadDefaultConfig={loadDefaultConfig}
              configList={configList}
              auth={auth}
              update={update}
              onChange={onPageChange}
              page={page}
              total={total}
              loading={loading}
              defaultConfig={defaultConfig}
            />
          </>}
      </PlatformContext.Provider>
    </Card>
  );
};

export default AppDeliverConfig;
